import React, { useState, useRef } from 'react';

const BUDGET_LEVELS = [
  { key: 'zero', label: 'None', color: '#e0e0e0', description: 'No budget' },
  { key: 'low', label: 'Low', color: '#e57373', description: 'Basic options' },
  { key: 'mid', label: 'Mid', color: '#5ec2b7', description: 'Standard options' },
  { key: 'high', label: 'High', color: '#bfa76a', description: 'Premium options' },
  { key: 'luxury', label: 'Luxury', color: '#C28811', description: 'Top-tier options' }
];

export const US_COST_RANGES = {
  venue: { zero: 0, low: 5000, mid: 12000, high: 20000, luxury: 30000 },
  catering: { zero: 0, low: 40, mid: 75, high: 125, luxury: 200 },
  photographer: { zero: 0, low: 1500, mid: 3000, high: 5000, luxury: 8000 },
  dj: { zero: 0, low: 800, mid: 2000, high: 4000, luxury: 7000 },
  flowers: { zero: 0, low: 1000, mid: 5000, high: 15000, luxury: 50000 },
  transport: { zero: 0, low: 500, mid: 1200, high: 2500, luxury: 5000 }
};

export const MEXICO_COST_RANGES = {
  venue: { zero: 0, low: 3000, mid: 7000, high: 12000, luxury: 20000 },
  catering: { zero: 0, low: 25, mid: 55, high: 90, luxury: 150 },
  photographer: { zero: 0, low: 800, mid: 2000, high: 3500, luxury: 6000 },
  dj: { zero: 0, low: 600, mid: 1500, high: 2500, luxury: 4000 },
  flowers: { zero: 0, low: 800, mid: 3000, high: 10000, luxury: 20000 },
  transport: { zero: 0, low: 300, mid: 800, high: 1500, luxury: 3000 }
};

const SNAP_VALUES = [0, 25, 50, 75, 100];

function formatCurrency(num) {
  return num.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function getLevelIdx(value) {
  if (value <= 0) return 0;
  if (value <= 25) return 1;
  if (value <= 50) return 2;
  if (value <= 75) return 3;
  return 4;
}

export default function CategoryBudgetControls({ category, value, onChange, costRanges, guestCount, unit }) {
  console.log('CategoryBudgetControls props:', { category, value, onChange, costRanges, guestCount });
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef(null);
  if (typeof value !== 'number') {
    console.error('CategoryBudgetControls: value is not a number', value, 'category:', category);
    return null;
  }
  const usRange = costRanges?.us || {};
  const mexicoRange = costRanges?.mexico;
  if (!usRange) return null;

  // Only calculate Mexico value if mexicoRange exists
  const interpolateCost = (budgetLevel, ranges) => {
    if (!ranges) return 0;
    if (budgetLevel <= 0) return ranges.zero;
    if (budgetLevel <= 25) return ranges.low;
    if (budgetLevel <= 50) return ranges.mid;
    if (budgetLevel <= 75) return ranges.high;
    return ranges.luxury;
  };
  const currentUsValue = interpolateCost(value, usRange);
  const currentMexicoValue = mexicoRange ? interpolateCost(value, mexicoRange) : null;
  const savings = currentMexicoValue !== null ? currentUsValue - currentMexicoValue : null;
  const savingsPercent = (currentMexicoValue !== null && currentUsValue > 0)
    ? Math.round((savings / currentUsValue) * 100)
    : null;

  // For bar and catering, calculate per person
  let displayValue = currentUsValue;
  let perPerson = null;
  if ((unit === 'per guest' || category === 'catering' || category === 'bar') && guestCount) {
    perPerson = currentUsValue;
    displayValue = currentUsValue * guestCount;
  }

  const levelIdx = getLevelIdx(value);
  const currentLevel = BUDGET_LEVELS[levelIdx];

  // Slider track fill color
  const fillColor = '#C28811'; // soft gold
  const trackBg = '#ececec';
  const sliderSteps = [0, 25, 50, 75, 100];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-1">
        <div className="flex items-center gap-2">
          <label className="font-medium text-[#181c24]">{category.charAt(0).toUpperCase() + category.slice(1)}</label>
          {savingsPercent !== null && (
            <span className="text-sm text-[#bfa76a] font-medium">
              {savingsPercent}% savings
            </span>
          )}
        </div>
        <div className="flex flex-col items-end">
          <div className="flex items-center gap-2 min-w-[90px] justify-end">
            <span className="text-xs text-[#888]">Budget:</span>
            <span className="text-base font-bold text-[#bfa76a]">
              ${displayValue?.toLocaleString()}
            </span>
          </div>
          {(unit === 'per guest' || category === 'catering' || category === 'bar') && perPerson !== null && (
            <span className="text-xs text-[#888] mt-0.5">
              (${perPerson.toLocaleString()} × {guestCount} guests)
            </span>
          )}
        </div>
      </div>

      {/* Modern continuous slider with value pill above handle */}
      <div className="relative w-full flex flex-col items-center pt-16 pb-2">
        <input
          ref={sliderRef}
          type="range"
          min={0}
          max={100}
          step={25}
          value={value}
          onChange={e => onChange(Number(e.target.value))}
          onMouseDown={() => setIsDragging(true)}
          onMouseUp={() => setIsDragging(false)}
          onTouchStart={() => setIsDragging(true)}
          onTouchEnd={() => setIsDragging(false)}
          className="w-full h-2 rounded-full appearance-none bg-[#ececec] focus:outline-none focus:ring-2 focus:ring-[#bfa76a] transition-all"
          style={{
            background: `linear-gradient(to right, ${fillColor} 0%, ${fillColor} ${value}%, ${trackBg} ${value}%, ${trackBg} 100%)`,
          }}
        />
        {/* Custom thumb styling */}
        <style>{`
          input[type='range']::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 28px;
            height: 28px;
            border-radius: 50%;
            background: #fff;
            border: 3px solid #C28811;
            box-shadow: 0 2px 8px 0 rgba(191,167,106,0.12);
            cursor: pointer;
            transition: box-shadow 0.2s;
            margin-top: -10px;
          }
          input[type='range']:focus::-webkit-slider-thumb {
            box-shadow: 0 0 0 4px #f3e6c6;
          }
          input[type='range']::-moz-range-thumb {
            width: 28px;
            height: 28px;
            border-radius: 50%;
            background: #fff;
            border: 3px solid #C28811;
            box-shadow: 0 2px 8px 0 rgba(191,167,106,0.12);
            cursor: pointer;
            transition: box-shadow 0.2s;
            margin-top: -10px;
          }
          input[type='range']:focus::-moz-range-thumb {
            box-shadow: 0 0 0 4px #f3e6c6;
          }
          input[type='range']::-ms-thumb {
            width: 28px;
            height: 28px;
            border-radius: 50%;
            background: #fff;
            border: 3px solid #C28811;
            box-shadow: 0 2px 8px 0 rgba(191,167,106,0.12);
            cursor: pointer;
            transition: box-shadow 0.2s;
            margin-top: -10px;
          }
          input[type='range']:focus::-ms-thumb {
            box-shadow: 0 0 0 4px #f3e6c6;
          }
          input[type='range']::-webkit-slider-runnable-track {
            height: 8px;
            border-radius: 4px;
          }
          input[type='range']::-ms-fill-lower {
            background: ${fillColor};
          }
          input[type='range']::-ms-fill-upper {
            background: ${trackBg};
          }
        `}</style>
        {/* Budget level labels below, spaced evenly */}
        <div className="flex justify-between w-full mt-4 text-xs">
          {BUDGET_LEVELS.map((level, idx) => (
            <span
              key={level.key}
              className={levelIdx === idx ? 'font-bold text-[#181c24]' : 'text-[#888]'}
              style={{ color: levelIdx === idx ? level.color : undefined }}
            >
              {level.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
} 